// @ts-nocheck
import { AxiosSingular } from '@deleteagency/axios-singular';
import { DcBaseComponent } from '@deleteagency/dc';

import axios from 'axios';
import api from 'general/js/api';
import ArrayHelper from 'general/js/array-helper';
import { GoogleMap } from 'general/js/google-maps';
import { BaseMarker, onMapsReady, OutsidePropertyMarker } from '../../map';
import { eventBus, events } from 'general/js/events';

const DEFAULT_ZOOM = 12;
// streets-buildings
const MAX_ZOOM = 19;
// continents level
const MIN_ZOOM = 1;

export default class SearchMapComponent extends DcBaseComponent {
    constructor(element) {
        super(element);
        this.afterIdleTimeout = null;
        this.loadingTime = 200;
        this.isBoundsSetted = false;
        this.axiosSingular = new AxiosSingular(api, axios.CancelToken, axios.isCancel);
        this.endpointUrl = this.options.mapParams.endpointUrl;
        this.isChangeable = this.options.mapParams.isChangeable;
        this.locations = [];
        this.outOfAreaEndPointUrl = this.options.mapParams.outOfAreaEndPointUrl;
        this.enableOutOfArea = this.options.mapParams.enableOutOfArea;
        this.moveListener = null;
    }

    static getNamespace() {
        return 'search-map';
    }

    onInit() {
        this._startMap();
    };
    
    async _startMap() {
        
            this.defaultMarkerOptions = {};
            this.defaultPolygonOptions = {
                strokeColor: '#0F143C',
                strokeOpacity: 1,
                strokeWeight: 2,
                fillOpacity: .2,
                fillColor: '#0F143C',
            };
            this.defaultCircleOptions = {
                strokeColor: '#0F143C',
                strokeOpacity: 1,
                strokeWeight: 2,
                fillOpacity: 0.2,
                fillColor: '#0F143C',
            };
    
            this.markers = this.options.mapParams.markers || [];
            this.polygons = this.options.mapParams.polygons || [];
            this.circles = this.options.mapParams.circles || [];
            this.selectedOptions = this.options.selectedOptions || {};
    
            this.map = new GoogleMap(
                this.refs.map,
                {
                    zoom: DEFAULT_ZOOM,
                    maxZoom: MAX_ZOOM,
                    minZoom: MIN_ZOOM,
                    disableDefaultUI: true,
                },
                {
                    fitPaddings: {
                        top: 10,
                        bottom: 10,
                        left: 10,
                        right: 10,
                    },
                    fitPopupPaddings: {
                        top: 10,
                        bottom: 10,
                        left: 10,
                        right: 10,
                    },
                    initPromise: () => new Promise((resolve) => onMapsReady(resolve)),
                    createMarker: (options) => new BaseMarker(options),
                    autoFitOnRender: false,
                }
            );
    
            this.map.setPolygons(
                this.polygons.map((polygon) => ({
                    ...this.defaultPolygonOptions,
                    ...polygon,
                }))
            );
            this.map.setCircles(
                this.circles.map((circle) => ({
                    ...this.defaultCircleOptions,
                    ...circle,
                }))
            );
    
            this.mapInstance = await this.map.getInstance();
            this.attachEvents();
            this._addZoomControls();
            this.afterMapLoaded();
            
    }

    attachEvents() {
        google.maps.event.addListenerOnce(this.mapInstance, 'idle', () => {
            this._renderInitialMarkers();
        });

        if (!this.enableOutOfArea) {
            this._onMoveMap();
        }
    }

    _addZoomControls() {
        const { zoomIn, zoomOut, zoomControl } = this.refs;

        this.addListener(zoomIn, 'click', this._zoomIn);
        this.addListener(zoomOut, 'click', this._zoomOut);

        this.mapInstance.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControl);
    }

    _zoomIn = () => this.mapInstance.setZoom(this.mapInstance.getZoom() + 1);

    _zoomOut = () => this.mapInstance.setZoom(this.mapInstance.getZoom() - 1);

    _onMoveMap = () => {
        this.moveListener = google.maps.event.addListener(
            this.mapInstance,
            'bounds_changed',
            () => {
                this.onBoundsChanged();
            }
        );
    };

    onBoundsChanged() {
        if (this.isBoundsSetted) {
            this.isBoundsSetted = false;
            return;
        }
        if (this.afterIdleTimeout) {
            clearTimeout(this.afterIdleTimeout);
        }
        this.afterIdleTimeout = setTimeout(() => {
            const bounds = this.getFormattedBounds();
            this.updateData(bounds);
        }, this.loadingTime);
    }

    _reloadMap(){
        const bounds = this.getFormattedBounds();
        this.updateData(bounds);
    }

    async updateData(bounds) {
        this.refs.spinner.classList.remove('is-inactive');

        const locations = await this.fetchLocations(bounds, this.endpointUrl);

        if (ArrayHelper.isEmpty(locations)) return;

        this.updateMarkers(locations);
    }

    updateMarkers(locations) {
        const markers = this._getMarkers(locations);
        this.map.setMarkers(markers.map((marker) => this.getMarkerOptions(marker)));
        this.map.render();
        this.isBoundsSetted = true;
    }

    _getMarkers = (locations, isOutside = false) => {
        const markers = [];
    
        locations?.forEach((location) => {
            const isViewed = this._isMarkerViewed(location.externalID);
            markers.push({
                position: { lat: location.latitude, lng: location.longitude },
                popupId: `marker-${location.externalID}`,
                isOutside,
                locationId: location.externalID,
                endpointUrl: this.endpointUrl,
                numericId: location.numericExternalID,
                selectedOptions: this.selectedOptions,
                locationData: location,
                isViewed: isViewed,
                allUnseen: sessionStorage.getItem('winkworth_search_results_map__all_unseen'),
            });
        });
        
        const allUnseen = markers.every(marker => marker.isViewed === false);
        if (allUnseen) {
            markers.forEach(marker => marker.allUnseen = true);
        } else {
            markers.forEach(marker => marker.allUnseen = false);
        }   
        return markers;
    };

    _isMarkerViewed(markerId) {
        const propertyId = markerId;
        const viewedProperties = sessionStorage.getItem('winkworth_search_results_map__viewed_properties');
        if (!viewedProperties) return false;
        const viewedList = JSON.parse(viewedProperties);
        return viewedList.some(prop => prop.propertyId === String(propertyId));        
    }

    afterMapLoaded() {
        this.isBoundsSetted = true;
        this.map.getInstance().then((map) => {
            this.map._fitAll(map);
        });
    }

    async _renderInitialMarkers() {        
        this.refs.spinner.classList.remove('is-inactive');

        if (true) {
            const [locations, outAreaLocations] = await Promise.all(
                [
                    await this.fetchLocations({}, this.endpointUrl),
                    ...(this.enableOutOfArea
                        ? [await this.fetchLocations({}, this.outOfAreaEndPointUrl)]
                        : [undefined]),
                ].filter(Boolean)
            );

            const inAreaMarkers = this._getMarkers(locations);
            let outOfAreaMarkers = [];
            if (this.enableOutOfArea) {
                outOfAreaMarkers = this._getMarkers(outAreaLocations, true);
            }
            // Set markers on the map
            this.map.setMarkers(
                [
                    ...inAreaMarkers,
                    ...(this.enableOutOfArea ? outOfAreaMarkers : []),
                ].map((marker) => this.getMarkerOptions(marker))
            );
            this.map.render();
            this.afterMapLoaded();
        }
    }


    getMapBounds() {
        const bounds = this.mapInstance.getBounds();

        if (!bounds) return false;

        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        return {
            northeast: {
                latitude: northEast.lat(),
                longitude: northEast.lng(),
            },
            southwest: {
                latitude: southWest.lat(),
                longitude: southWest.lng(),
            },
        };
    }

    getFormattedBounds() {
        const bounds = this.getMapBounds();

        return {
            neLatitude: bounds.northeast.latitude,
            neLongitude: bounds.northeast.longitude,
            swLatitude: bounds.southwest.latitude,
            swLongitude: bounds.southwest.longitude,
        };
    }

    getMarkerOptions(marker) {
        return {
            ...marker,
            ...this.defaultMarkerOptions,
            popupElement: document.getElementById(marker.popupId),
            createMarker: (options) =>
                marker.isOutside ? new OutsidePropertyMarker(options) : new BaseMarker(options),
        };
    }

    fetchLocations(bounds, endpointUrl) {
        return this.axiosSingular
            .get(endpointUrl, {
                params: {
                    ...this.selectedOptions,
                    ...bounds,
                },
                paramsSerializer: (params) => {
                    if (!params) return '';
                    const filteredParams = Object.keys(params)
                        .map((key) => {
                            const value = params[key];
                            if (Array.isArray(value)) {
                                // If the value is an array, construct the parameter string with index
                                const arrayParam = value.map((element, index) => {                                        
                                    return `${encodeURIComponent(key)}[${index}]=${encodeURIComponent(element)}`;
                                }).join('&');
                                return arrayParam;
                            } else if (value === null || value === undefined || value === '') {
                                // Remove empty values
                                return '';
                            } else {
                                return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                            }
                        })
                        .filter((param) => param.length > 0)
                        .join('&');
                    return filteredParams;
                }
            })
            .then(
                (response) => response.data,
                (error) => {
                    if (!axios.isCancel(error)) {
                        // eslint-disable-next-line no-console
                        console.error('Actual error!', error);
                    }
                }
            )
            .finally(() => {
                this.refs.spinner.classList.add('is-inactive');
            });
    }

}
